/**
 * Constancia: de los periodos ya cerrados en un rango, cuántos se cumplieron. Lo usan Hoy,
 * Progreso y el asistente, con la misma regla: hoy no cuenta (sigue abierto) y los días
 * omitidos o de vacaciones no penalizan.
 */
import { addDays, type EngineSettings, type LocalDate } from '@habitos/engine';
import type { HabitRec, VacationRec } from './model';
import { evaluate, liveHabits, type HabitIndex } from './selectors';

export const EVALUABLE = new Set(['completed', 'missed', 'failed']);

export interface HabitRate {
  habit: HabitRec;
  done: number;
  total: number;
  rate: number | null;
}

/** Hábitos activos (no tareas) con sus periodos cerrados en [from, to]. */
export function habitRates(habits: HabitRec[], idx: HabitIndex, vacations: VacationRec[], settings: EngineSettings, today: LocalDate, from: LocalDate, to: LocalDate): HabitRate[] {
  return liveHabits(habits)
    .filter((h) => h.kind !== 'task' && !h.archivedAt)
    .map((h) => {
      const rs = evaluate(h, idx, vacations, settings, today, { from, to }).filter((r) => !r.open && r.end <= to && EVALUABLE.has(r.status));
      const done = rs.filter((r) => r.status === 'completed').length;
      return { habit: h, done, total: rs.length, rate: rs.length ? done / rs.length : null };
    });
}

/** Constancia global de los últimos `days` días cerrados (sin contar hoy). */
export function consistency(habits: HabitRec[], idx: HabitIndex, vacations: VacationRec[], settings: EngineSettings, today: LocalDate, days = 7): { done: number; total: number; rate: number | null; from: LocalDate; to: LocalDate } {
  const to = addDays(today, -1);
  const from = addDays(today, -days);
  const rows = habitRates(habits, idx, vacations, settings, today, from, to);
  const done = rows.reduce((n, r) => n + r.done, 0);
  const total = rows.reduce((n, r) => n + r.total, 0);
  return { done, total, rate: total ? done / total : null, from, to };
}
